/**
 * Vector tiles for datasets too large for in-browser GeoJSON (docs/VECTOR_TILES.md).
 *
 * The switch is one function, `chooseLayerMode(metadata)`: a dataset with more than
 * VT_GEOJSON_MAX_FEATURES features AND ready tiles is drawn from the MVT endpoint;
 * everything else keeps the GeoJSON paths. Paint and popup helpers mirror the GeoJSON
 * layers (hooks/useDatasetRawLayers.js, useMapLayers.xyzPaint), but read their colour
 * ranges from the TileJSON `stats` because a tile only holds a slice of the data.
 */
import api from './api';
import { apiBaseUrl } from './portal';
import { jobsApi } from './jobs-api';
import { rhoColorExpr, electrodePaint, rawPopupProps } from '../hooks/useDatasetRawLayers';

export const VT_GEOJSON_MAX_FEATURES = Number(process.env.REACT_APP_VT_GEOJSON_MAX_FEATURES) || 20000;

// Draw order: the dense point layers first, electrodes on top.
export const VT_KINDS = ['pseudo_point', 'xyz_point', 'electrode'];
const RAMP = ['#2563eb', '#00b8de', '#34d399', '#fbbf24', '#f87171'];
const INK = '#0b1220';

/** 'vector' when the dataset is above the threshold and its tiles are ready, else 'geojson'. */
export function chooseLayerMode(metadata, threshold = VT_GEOJSON_MAX_FEATURES) {
  const count = Number(metadata?.feature_count) || 0;
  const tiles = metadata?.tiles;
  if (count > threshold && tiles?.ready && Object.keys(tiles.kinds || {}).length) return 'vector';
  return 'geojson';
}

/** Above the threshold but without ready tiles: offer "Build vector tiles". */
export function tilesPending(metadata, threshold = VT_GEOJSON_MAX_FEATURES) {
  return (Number(metadata?.feature_count) || 0) > threshold && !metadata?.tiles?.ready;
}

/** The kinds with tiles, in draw order. */
export function tileKinds(metadata) {
  const kinds = metadata?.tiles?.kinds || {};
  return VT_KINDS.filter(k => kinds[k]);
}

export const tileUrl = (datasetId, kind, base = apiBaseUrl()) =>
  `${base}/api/datasets/${datasetId}/tiles/${kind}/{z}/{x}/{y}.pbf`;

export const vtApi = {
  tileJson: (datasetId, kind) => api.get(`/api/datasets/${datasetId}/tiles/${kind}.json`),
  build: (datasetId) => api.post(`/api/datasets/${datasetId}/tiles/build`),
  pmtilesUrl: (datasetId, kind) => api.get(`/api/datasets/${datasetId}/tiles/${kind}.pmtiles`),
  exportPmtiles: (datasetId, kind) =>
    jobsApi.submit({ kind: 'pmtiles_export', params: { dataset_id: datasetId, kind }, dataset_id: datasetId }),
};

// ── paint ───────────────────────────────────────────────────────────────────

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

/** 5 resistivity stops, log-spaced between the field's p02 and p98 (as rhoStops). */
export function rhoStopsFromStats(stat) {
  const lo0 = [stat?.p02, stat?.min].find(v => finite(v) && v > 0);
  const hi0 = [stat?.p98, stat?.max].find(v => finite(v) && v > 0);
  if (!lo0 || !hi0) return [1, 10, 100, 1000, 10000];
  let lo = Math.log10(lo0), hi = Math.log10(hi0);
  if (!(hi > lo)) hi = lo + 1;
  return [0, 1, 2, 3, 4].map(i => 10 ** (lo + ((hi - lo) * i) / 4));
}

/** The z field an XYZ tile layer is coloured by: the first mapped z field with stats. */
export function defaultZField(tj) {
  const stats = tj?.stats || {};
  return (tj?.z_fields || []).find(f => stats[f]) || Object.keys(stats).find(f => f !== '_row') || null;
}

export function xyzTilePaint(tj, field = defaultZField(tj)) {
  const s = tj?.stats?.[field];
  if (!s) return { 'circle-radius': 3, 'circle-color': '#a78bfa', 'circle-opacity': 0.9 };
  const min = finite(s.p02) ? s.p02 : s.min;
  const maxRaw = finite(s.p98) ? s.p98 : s.max;
  const max = maxRaw > min ? maxRaw : min + 1;
  const t = (f) => min + (max - min) * f;
  return {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 16, 4, 20, 6],
    'circle-color': ['interpolate', ['linear'], ['to-number', ['get', field], min],
      min, RAMP[0], t(0.25), RAMP[1], t(0.5), RAMP[2], t(0.75), RAMP[3], max, RAMP[4]],
    'circle-opacity': 0.9,
    'circle-stroke-color': INK,
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 16, 0.6],
  };
}

/** Same encoding as pseudosectionPaint: resistivity ramp, QC tier D hollow. */
export function pseudoTilePaint(tj) {
  const color = rhoColorExpr(rhoStopsFromStats(tj?.stats?.apparent_resistivity));
  const hollow = ['==', ['get', 'tier'], 'D'];
  return {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 16, 4, 20, 6],
    'circle-color': ['case', hollow, 'rgba(0,0,0,0)', color],
    'circle-opacity': 0.9,
    'circle-stroke-color': ['case', hollow, color, INK],
    'circle-stroke-width': ['case', hollow, 1.5, 0.5],
  };
}

export function vtPaint(kind, tj, { zField } = {}) {
  if (kind === 'pseudo_point') return pseudoTilePaint(tj);
  if (kind === 'electrode') return electrodePaint();
  return xyzTilePaint(tj, zField || defaultZField(tj));
}

// ── layers and popups ───────────────────────────────────────────────────────

const LABELS = { pseudo_point: 'Pseudosection', electrode: 'Electrodes', xyz_point: 'Points' };

/** [[w, s], [e, n]] from TileJSON bounds, or null. */
export function tileJsonBounds(tj) {
  const b = tj?.bounds;
  return Array.isArray(b) && b.length === 4 && b.every(finite) ? [[b[0], b[1]], [b[2], b[3]]] : null;
}

/**
 * Everything VectorTileLayers needs to render one kind. `pmtilesUrl` (a signed URL)
 * switches the source to the exported archive through the pmtiles:// protocol.
 */
export function vtLayerSpec(datasetId, kind, tj, { name, pmtilesUrl, zField, base } = {}) {
  const id = `vt-${datasetId}-${kind}`;
  const source = pmtilesUrl
    ? { type: 'vector', url: `pmtiles://${pmtilesUrl}` }
    : {
      type: 'vector',
      tiles: [tileUrl(datasetId, kind, base)],
      minzoom: tj?.minzoom ?? 0,
      maxzoom: tj?.maxzoom ?? 16,
      ...(tj?.bounds ? { bounds: tj.bounds } : {}),
    };
  return {
    id, kind, datasetId,
    sourceId: `${id}-src${pmtilesUrl ? '-pm' : ''}`,
    layerId: `${id}-pts`,
    sourceLayer: kind,
    source,
    paint: vtPaint(kind, tj, { zField }),
    name: `${LABELS[kind] || kind}${name ? ` — ${name}` : ''} (tiles)`,
    featureCount: tj?.feature_count ?? null,
    bounds: tileJsonBounds(tj),
    zFields: kind === 'xyz_point' ? (tj?.z_fields || []) : [],
    zField: kind === 'xyz_point' ? (zField || defaultZField(tj)) : null,
    visible: true,
  };
}

const round = (v, n) => (typeof v === 'number' && Number.isFinite(v) ? Number(v.toPrecision(n)) : v);

/** Popup rows for a tile feature (MVT has no arrays: flags arrive comma-separated). */
export function vtPopupProps(kind, p = {}) {
  if (kind === 'pseudo_point') {
    const flags = typeof p.flags === 'string' && !p.flags.startsWith('[')
      ? p.flags.split(',').filter(Boolean) : p.flags;
    return rawPopupProps('pseudosection', { ...p, flags });
  }
  if (kind === 'electrode') return rawPopupProps('electrodes', p);
  const out = {};
  Object.entries(p).forEach(([k, v]) => { if (k !== '_row') out[k] = round(v, 6); });
  return out;
}

/** Union of the specs' bounds as [[w, s], [e, n]], or null. */
export function unionBounds(specs) {
  const bs = specs.map(s => s.bounds).filter(Boolean);
  if (!bs.length) return null;
  return [
    [Math.min(...bs.map(b => b[0][0])), Math.min(...bs.map(b => b[0][1]))],
    [Math.max(...bs.map(b => b[1][0])), Math.max(...bs.map(b => b[1][1]))],
  ];
}
